import {
  CampaignUrlFieldId,
  CampaignUrlFieldName,
  RegExpCNameUrls,
  RegExpMobizCampaignUrls,
  ValidColumnNameRegExpStr
} from '../constants';
import { CampaignDefaultFields } from '../interfaces/campaign';
import {
  escapePersonalizedFieldValues,
  getFieldNameToIdMapping
} from './campaignDefaultFields';
import { escapeRegExp } from './string';

const PersonalizationTagRegex = /{{\s*(\w+)\s*}}/g;
const CampaignUrlTagRegex = /\${ (\w+) }/g;
export class SmsHtmlParser {
  static createTemplateLiteral(
    html: string,
    fieldData: CampaignDefaultFields,
    includeCampaignUrl = true
  ): string {
    const fieldNameToIdMapping = getFieldNameToIdMapping(fieldData);

    return html.replace(
      new RegExp(`{{\\s*(${ValidColumnNameRegExpStr})\\s*}}`, 'g'),
      (match, matchedFieldName) => {
        const fieldId = (() => {
          if (fieldNameToIdMapping[matchedFieldName]) {
            return fieldNameToIdMapping[matchedFieldName];
          } else if (
            includeCampaignUrl &&
            matchedFieldName === CampaignUrlFieldName
          ) {
            return CampaignUrlFieldId;
          }
        })();
        return fieldId ? `\${ ${fieldId} }` : match;
      }
    );
  }

  static trimNewLineCharacters(text: string): string {
    return text
      .replace(/\n+$|(\r\n)+$|\r+$/, '')
      .replace(/^\n+|^(\r\n)+|^\r+/, '');
  }

  static removeParagraphsAndLineBreaks(html: string): string {
    return SmsHtmlParser.trimNewLineCharacters(
      html
        .replace(/<br>/g, '')
        .replace(/<\/p><p>/g, '\n')
        .replace(/<\/p>|<p>/g, '')
    );
  }

  static removeParagraphsClassesNewLinesAndLineBreaks(html: string): string {
    return SmsHtmlParser.trimNewLineCharacters(
      html
        .replace(/<br>/g, '')
        .replace(/<\/p><p>/g, '')
        .replace(/<\/p>|<p>/g, '')
        .replace(/<p class="[a-zA-z0-9-_\s]*">/g, '')
        .replace(/<span class="[a-zA-z0-9-_\s]*">/g, '')
        .replace(/<span>|<\/span>/g, '')
    );
  }

  // Replace instances of '${ columnId }' in the `templateLiteral` string with
  // the value of the 'columnId' property in the `interpolationObject`.
  //
  // This is used instead of lodash's `template` function because internally
  // the `template` function transforms the provided object's keys into
  // local variables. This means any key that is not a valid variable name
  // (such as a uuid column id that begins with a number) will cause an error.
  static interpolateTemplateLiteral(
    templateLiteral: string,
    interpolationObject: Record<string, string>
  ): string {
    return templateLiteral
      .replace(
        CampaignUrlTagRegex,
        (_match: string, matchedId: string) => interpolationObject[matchedId]
      )
      .replace(
        PersonalizationTagRegex,
        (_match: string, matchedId: string) => interpolationObject[matchedId]
      );
  }

  static formatSmsTextForPreview(
    text: string,
    fieldValues: Record<string, string>,
    campaignUrl: string,
    linkElement: string,
    hasCustomDomain: boolean
  ) {
    let output = SmsHtmlParser.removeParagraphsAndLineBreaks(text);
    output = SmsHtmlParser.escapeHtml(output);

    const escapedFieldValues = escapePersonalizedFieldValues(fieldValues);
    output = SmsHtmlParser.interpolateTemplateLiteral(output, {
      ...escapedFieldValues,
      campaignUrl
    });

    // Valid Mobiz campaignUrl is one that has a space in front and back of it
    // unless it is in the beginning or the end.
    // We add the space(s) back if the match contained space(s).
    return output.replace(
      hasCustomDomain ? RegExpCNameUrls : RegExpMobizCampaignUrls,
      match => {
        return (
          (match.startsWith(' ') ? ' ' : '') +
          linkElement +
          (match.endsWith(' ') ? ' ' : '')
        );
      }
    );
  }

  static escapeHtml(str: string) {
    //check if escaped
    const isEscaped = /&[a-z]+;/i.test(str);
    if (isEscaped) {
      return str;
    } else {
      return str?.replace(/[&<>"']/g, char => {
        switch (char) {
          case '&':
            return '&amp;';
          case '<':
            return '&lt;';
          case '>':
            return '&gt;';
          case '"':
            return '&quot;';
          case "'":
            return '&#39;';
          default:
            return char;
        }
      });
    }
  }

  static removeTag(
    html: string,
    fieldId: string,
    fieldData: CampaignDefaultFields
  ): string {
    if (fieldId) {
      const escapedFieldName = escapeRegExp(fieldId);
      return html.replace(
        new RegExp(`{{\\s*${escapedFieldName}\\s*}}`, 'g'),
        ''
      );
    } else {
      return html;
    }
  }

  static getInsertedFieldIds(
    html: string,
    fieldData: CampaignDefaultFields
  ): string[] {
    return Object.entries(fieldData)
      .filter(([id, _value]) => {
        const escapedFieldName = escapeRegExp(id);
        return html.match(new RegExp(`{{\\s*${escapedFieldName}\\s*}}`));
      })
      .map(([fieldId]) => fieldId);
  }

  static containsCampaignUrl(url: string, isCName: boolean): boolean {
    return (
      url.includes(`mo.biz/s/xxxxx`) ||
      url.includes(`mobz.ly/s/xxxxx`) ||
      (isCName && url.includes(`/s/xxxxx`))
    );
  }

  static getCampaignLinkElement(html: string): Element | null {
    const dummy = document.createElement('div');
    dummy.innerHTML = html;
    const campaignLink = dummy.querySelector(
      `a[title='${CampaignUrlFieldName}']`
    );
    return campaignLink;
  }

  static hasNoSpaceAroundCampaignLinkElement(html: string): boolean {
    const dummy = document.createElement('div');
    dummy.innerHTML = html;
    const campaignLink = dummy.querySelector(
      `a[title='${CampaignUrlFieldName}']`
    );

    if (!campaignLink) return false;

    // if there are no space before & after the campaign link, it's invalid
    const [left, right] = dummy.innerHTML.split(campaignLink.outerHTML);
    if (
      (left && left === left.trimEnd()) ||
      (right && right === right.trimStart())
    ) {
      return true;
    }

    return false;
  }
}
